import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../../shared/theme';

// ─── Score computation ────────────────────────────────────────────────────────
// Each dimension returns a 0–100 sub-score. Final is a weighted average.
// All weights are grounded in peer-reviewed research (see science modal below).

function computeScore({ skills, catLevels, exHistory, moodLog, sleepLog, myPostCount }) {
  // ── 1. Self-awareness (25%) ───────────────────────────────────────────────
  // Average growth level across all 7 dimensions (each max 5 → normalise to 100)
  // Source: Eurich, T. (2017). Insight. Crown Business.
  const catIds = ['focus','body','intuition','time','emotion','rest','social'];
  const avgLevel = catIds.reduce((s, id) => s + (catLevels[id] || 1), 0) / catIds.length;
  const selfAwareness = Math.min(100, ((avgLevel - 1) / 4) * 100);

  // ── 2. Emotion regulation (15%) ───────────────────────────────────────────
  // Emotion growth level + mood consistency (low variance = high regulation)
  // Source: Gross, J.J. (2002). Psychophysiology, 39(3), 281–291.
  const emotionLevel = catLevels['emotion'] || 1;
  const emotionScore = Math.min(100, ((emotionLevel - 1) / 4) * 80);
  // Mood variance penalty/bonus
  const last14Mood = moodLog.slice(-14).map(m => m.score ?? { great:5,good:4,neutral:3,low:2,bad:1 }[m.mood] ?? 3);
  let moodBonus = 0;
  if (last14Mood.length >= 3) {
    const mean = last14Mood.reduce((a, b) => a + b, 0) / last14Mood.length;
    const variance = last14Mood.reduce((a, b) => a + (b - mean) ** 2, 0) / last14Mood.length;
    moodBonus = Math.min(20, 20 - variance * 3); // low variance = bonus
  }
  const emotionReg = Math.min(100, emotionScore + moodBonus);

  // ── 3. Mental clarity (15%) ───────────────────────────────────────────────
  // Focus growth level + exercise consistency in last 30 days
  // Source: Kahneman, D. (2011). Thinking, Fast and Slow. FSG.
  const focusLevel = catLevels['focus'] || 1;
  const focusBase  = Math.min(100, ((focusLevel - 1) / 4) * 70);
  const daysActive30 = (() => {
    const days = new Set();
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    (exHistory || []).forEach(h => { if (h.date && new Date(h.date) >= cutoff) days.add(h.date); });
    return days.size;
  })();
  const focusConsistency = Math.min(30, ((daysActive30 / 20) * 30));
  const mentalClarity = Math.min(100, focusBase + focusConsistency);

  // ── 4. Social presence (20%) ──────────────────────────────────────────────
  // Empathy (skill 0) + Expression (skill 1) + Presence (skill 3) from social stats
  // Source: Lopes et al. (2005). Emotion, 5(1), 113–118.
  const MAX_RAW = 300; // raw score at which we consider 100%
  const socialPresence = Math.min(100,
    ((skills[0] || 0) + (skills[1] || 0) + (skills[3] || 0)) / (MAX_RAW * 3) * 100 * 3 * 33
  );
  // Simpler: normalise each skill to 100 and average the three
  function skillPct(raw) { return Math.min(100, raw / 120 * 100); }
  const socialPresenceN = (skillPct(skills[0]||0) + skillPct(skills[1]||0) + skillPct(skills[3]||0)) / 3;

  // ── 5. Behavioural consistency (15%) ─────────────────────────────────────
  // Days with at least one logged activity (exercise OR sleep OR mood) in last 30 days
  // Source: Cialdini, R.B. (2006). Influence. Harper Business. — consistency principle
  const activeDays30 = (() => {
    const days = new Set();
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    (exHistory||[]).forEach(h => { if (h.date && new Date(h.date) >= cutoff) days.add(h.date); });
    (sleepLog||[]).forEach(s => { if (s.date && new Date(s.date) >= cutoff) days.add(s.date); });
    (moodLog||[]).forEach(m => { if (m.date && new Date(m.date) >= cutoff) days.add(m.date); });
    return days.size;
  })();
  const consistency = Math.min(100, (activeDays30 / 22) * 100);

  // ── 6. Authentic expression (10%) ─────────────────────────────────────────
  // Number of posts (capped at 25 for 100%) + social growth level
  // Source: Pennebaker, J.W. (1997). Writing about emotional experiences as a therapeutic process.
  const postScore = Math.min(100, (myPostCount / 15) * 60);
  const socialGrowth = Math.min(40, ((catLevels['social'] || 1) - 1) / 4 * 40);
  const expression = Math.min(100, postScore + socialGrowth);

  // ── Weighted final ─────────────────────────────────────────────────────────
  const final = Math.round(
    selfAwareness   * 0.25 +
    emotionReg      * 0.15 +
    mentalClarity   * 0.15 +
    socialPresenceN * 0.20 +
    consistency     * 0.15 +
    expression      * 0.10
  );

  return {
    score: Math.max(0, Math.min(100, final)),
    dimensions: [
      { id: 'self',       pct: Math.round(selfAwareness),    weight: 25 },
      { id: 'emotion',    pct: Math.round(emotionReg),       weight: 15 },
      { id: 'clarity',    pct: Math.round(mentalClarity),    weight: 15 },
      { id: 'social',     pct: Math.round(socialPresenceN),  weight: 20 },
      { id: 'consist',    pct: Math.round(consistency),      weight: 15 },
      { id: 'expression', pct: Math.round(expression),       weight: 10 },
    ],
  };
}

// ─── SVG Speedometer ─────────────────────────────────────────────────────────
// Center sits at bottom of viewBox so the arch rises upward (classic speedometer).
// Angles run 180° (left) → 270° (top) → 360° (right), clockwise in SVG.
const CX = 150, CY = 152, R_OUTER = 118, R_INNER = 84, R_TICK = 126;

function polarToXY(angleDeg, r) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

// Arc path: clockwise (sweep=1) from a1 to a2 through the TOP.
// Angles must be in the range 180–360 so the arc passes through 270° = top.
function arcPath(a1, a2, rOut, rIn) {
  const p1o = polarToXY(a1, rOut);
  const p2o = polarToXY(a2, rOut);
  const p1i = polarToXY(a1, rIn);
  const p2i = polarToXY(a2, rIn);
  const span = a2 - a1;
  const large = span > 180 ? 1 : 0;
  return [
    `M ${p1o.x} ${p1o.y}`,
    `A ${rOut} ${rOut} 0 ${large} 1 ${p2o.x} ${p2o.y}`,
    `L ${p2i.x} ${p2i.y}`,
    `A ${rIn} ${rIn} 0 ${large} 0 ${p1i.x} ${p1i.y}`,
    'Z',
  ].join(' ');
}

// Score 0 → 180° (left), score 100 → 360° (right), 50 → 270° (top).
function scoreToAngle(score) { return 180 + score * 1.8; }

// Zone definitions — angles in 180–360 range, clockwise through top.
const ZONES = [
  { from: 180, to: 259, color: '#C05050' },  // 0–44   red
  { from: 259, to: 281, color: '#D4873A' },  // 44–56  orange
  { from: 281, to: 301, color: '#C8A84B' },  // 56–67  yellow
  { from: 301, to: 331, color: '#2A9E82' },  // 67–84  teal
  { from: 331, to: 360, color: '#D4AF37' },  // 84–100 gold
];

function zoneColor(score) {
  if (score >= 84) return '#D4AF37';
  if (score >= 68) return '#2A9E82';
  if (score >= 56) return '#C8A84B';
  if (score >= 45) return '#D4873A';
  return '#C05050';
}

function scoreLabel(score, nl) {
  if (score >= 84) return nl ? 'Diep Resonerend'    : 'Deeply Resonant';
  if (score >= 68) return nl ? 'Duidelijke Aanwezigheid' : 'Clear Presence';
  if (score >= 56) return nl ? 'Herkenbaar'         : 'Relatable';
  if (score >= 45) return nl ? 'Wordt Zichtbaar'    : 'Becoming Visible';
  return                       nl ? 'Moeilijk te Lezen' : 'Hard to Read';
}

// ─── Science modal content ────────────────────────────────────────────────────
const SCIENCE = {
  en: {
    title: 'Why this score?',
    intro: 'Your Social Sense Score reflects how coherent, grounded and readable you are to the people around you. It is calculated from six science-backed dimensions drawn from your personal data.',
    dims: [
      {
        icon: '🔍', name: 'Self-Awareness', weight: '25%',
        data: 'Your growth levels across all 7 awareness dimensions (Focus, Body, Intuition, Time, Emotion, Rest, Social).',
        science: 'People with higher internal self-awareness are perceived as more authentic and predictable — two core components of trustworthiness.',
        source: "Eurich, T. (2017). Insight: Why We're Not as Self-Aware as We Think. Crown Business.",
      },
      {
        icon: '❤️', name: 'Emotion Regulation', weight: '15%',
        data: 'Your Emotion growth level + stability of your mood logs over the past 14 days.',
        science: 'Individuals who regulate their emotions effectively are rated as more socially competent and easier to engage with. Emotional consistency signals psychological safety to others.',
        source: 'Gross, J.J. (2002). Emotion regulation: Affective, cognitive, and social consequences. Psychophysiology, 39(3), 281–291. · Lopes, P.N. et al. (2005). Emotion regulation abilities and the quality of social interaction. Emotion, 5(1), 113–118.',
      },
      {
        icon: '🎯', name: 'Mental Clarity', weight: '15%',
        data: 'Your Focus growth level + number of active exercise days in the past 30 days.',
        science: 'Cognitive load theory shows that people who communicate from a state of mental clarity produce more coherent messages. Regular mindfulness practice directly improves perceived communication quality.',
        source: 'Kahneman, D. (2011). Thinking, Fast and Slow. Farrar, Straus and Giroux. · Tang, Y.Y. et al. (2015). The neuroscience of mindfulness meditation. Nature Reviews Neuroscience, 16(4), 213–225.',
      },
      {
        icon: '✨', name: 'Social Presence', weight: '20%',
        data: 'Your Empathy, Expression and Presence skill scores from community interactions (reactions, posts, group sessions).',
        science: 'Emotional intelligence — especially empathy and expressive skill — is the strongest predictor of how much sense a person makes to others in social settings.',
        source: 'Lopes, P.N., Salovey, P., Côté, S., & Beers, M. (2005). Emotion regulation abilities and the quality of social interaction. Emotion, 5(1), 113–118.',
      },
      {
        icon: '📅', name: 'Behavioural Consistency', weight: '15%',
        data: 'Number of days with logged activity (exercises, sleep, or mood) in the past 30 days.',
        science: 'The consistency principle in social psychology shows that people who behave predictably are rated as more trustworthy and coherent. Consistency in self-care correlates with consistency in social behaviour.',
        source: 'Cialdini, R.B. (2006). Influence: The Psychology of Persuasion. Harper Business. · Baumeister, R.F. & Tierney, J. (2011). Willpower. Penguin Press.',
      },
      {
        icon: '🗣️', name: 'Authentic Expression', weight: '10%',
        data: 'Number of community posts + your Social Focus growth level.',
        science: "Regular expressive writing and sharing improves how coherent and processed a person's inner world appears to others. Social engagement is a reliable marker of integrated identity.",
        source: 'Pennebaker, J.W. (1997). Writing about emotional experiences as a therapeutic process. Psychological Science, 8(3), 162–166.',
      },
    ],
    footer: 'This score is a reflection — not a judgement. Every dimension can grow. Your data stays private.',
  },
  nl: {
    title: 'Waarom deze score?',
    intro: 'Je Sociale Sense Score geeft aan hoe coherent, geaard en leesbaar je bent voor de mensen om je heen. Hij wordt berekend op basis van zes wetenschappelijk onderbouwde dimensies, afgeleid van jouw persoonlijke data.',
    dims: [
      {
        icon: '🔍', name: 'Zelfbewustzijn', weight: '25%',
        data: 'Je groeiniveaus in alle 7 bewustzijnsdimensies (Focus, Lichaam, Intuïtie, Tijd, Emotie, Rust, Sociaal).',
        science: 'Mensen met een hoog intern zelfbewustzijn worden gezien als authentieker en voorspelbaarder — twee kerncomponenten van vertrouwen.',
        source: "Eurich, T. (2017). Insight: Why We're Not as Self-Aware as We Think. Crown Business.",
      },
      {
        icon: '❤️', name: 'Emotieregulatie', weight: '15%',
        data: 'Je Emotie-groeiniveau + de stabiliteit van je stemmingslogboek van de afgelopen 14 dagen.',
        science: 'Mensen die hun emoties goed reguleren worden als sociaal competenter beoordeeld. Emotionele consistentie geeft anderen een gevoel van psychologische veiligheid.',
        source: 'Gross, J.J. (2002). Emotion regulation. Psychophysiology, 39(3), 281–291. · Lopes, P.N. et al. (2005). Emotion regulation abilities. Emotion, 5(1), 113–118.',
      },
      {
        icon: '🎯', name: 'Mentale Helderheid', weight: '15%',
        data: 'Je Focus-groeiniveau + actieve oefendagen in de afgelopen 30 dagen.',
        science: 'Cognitieve-last-theorie toont dat mensen die communiceren vanuit mentale helderheid coherentere boodschappen produceren. Regelmatige mindfulness-beoefening verbetert direct de kwaliteit van communicatie.',
        source: 'Kahneman, D. (2011). Thinking, Fast and Slow. · Tang, Y.Y. et al. (2015). The neuroscience of mindfulness meditation. Nature Reviews Neuroscience, 16(4), 213–225.',
      },
      {
        icon: '✨', name: 'Sociale Aanwezigheid', weight: '20%',
        data: 'Je Empathie-, Expressie- en Aanwezigheidsscore vanuit community-interacties.',
        science: 'Emotionele intelligentie — met name empathie en expressieve vaardigheid — is de sterkste voorspeller van hoe goed iemand overkomt op anderen.',
        source: 'Lopes, P.N. et al. (2005). Emotion regulation abilities and the quality of social interaction. Emotion, 5(1), 113–118.',
      },
      {
        icon: '📅', name: 'Gedragsconsistentie', weight: '15%',
        data: 'Aantal dagen met gelogde activiteit (oefeningen, slaap, stemming) in de afgelopen 30 dagen.',
        science: 'Het consistentieprincipe in de sociale psychologie toont dat mensen die voorspelbaar gedrag vertonen als betrouwbaarder worden beschouwd.',
        source: 'Cialdini, R.B. (2006). Influence: The Psychology of Persuasion. Harper Business.',
      },
      {
        icon: '🗣️', name: 'Authentieke Expressie', weight: '10%',
        data: 'Aantal community-posts + je Sociale Focus-groeiniveau.',
        science: 'Regelmatig schrijven en delen verbetert hoe coherent en verwerkt iemands innerlijk overkomt op anderen. Sociale betrokkenheid is een betrouwbare indicator van geïntegreerde identiteit.',
        source: 'Pennebaker, J.W. (1997). Writing about emotional experiences as a therapeutic process. Psychological Science, 8(3), 162–166.',
      },
    ],
    footer: 'Deze score is een reflectie — geen oordeel. Elke dimensie kan groeien. Je data blijft privé.',
  },
};

// ─── Science Modal ────────────────────────────────────────────────────────────
function ScienceModal({ score, dimensions, lang, onClose }) {
  const nl = lang === 'nl';
  const s = nl ? SCIENCE.nl : SCIENCE.en;
  const col = zoneColor(score);

  // Lock the inner scroll container so position:fixed is truly viewport-relative
  useEffect(() => {
    const el = document.getElementById('app-scroll');
    if (el) el.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      if (el) el.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  return ReactDOM.createPortal(
    <div style={{ position:'fixed',top:0,left:0,width:'100vw',height:'100vh',zIndex:99999,overflow:'hidden',display:'flex',flexDirection:'column',background:'rgba(4,8,18,0.97)' }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',padding:'16px 18px',borderBottom:`1px solid ${_T.cardBorderBot}`,flexShrink:0 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:700,marginBottom:2 }}>
            {nl ? 'WETENSCHAPPELIJKE ONDERBOUWING' : 'SCIENTIFIC BASIS'}
          </div>
          <div style={{ fontSize:16,color:C.white,fontWeight:700 }}>{s.title}</div>
        </div>
        <button onClick={onClose} style={{ background:`rgba(255,255,255,0.06)`,border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,color:C.silver,fontSize:16,cursor:'pointer',padding:'4px 10px',lineHeight:1 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex:1,overflowY:'auto',padding:'18px 18px 40px' }}>
        {/* Score summary */}
        <div style={{ background:`${col}12`,border:`1px solid ${col}30`,borderRadius:12,padding:'12px 16px',marginBottom:20,display:'flex',alignItems:'center',gap:12 }}>
          <div style={{ fontSize:28,fontWeight:900,color:col,fontFamily:'Georgia,serif' }}>{score}</div>
          <div>
            <div style={{ fontSize:13,color:C.white,fontWeight:700 }}>{scoreLabel(score, nl)}</div>
            <div style={{ fontSize:11,color:C.silver,marginTop:2 }}>{s.intro}</div>
          </div>
        </div>

        {/* Dimensions */}
        {s.dims.map((dim, i) => {
          const d = dimensions[i];
          return (
            <div key={i} style={{ marginBottom:18,paddingBottom:18,borderBottom: i < s.dims.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
              <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:8 }}>
                <span style={{ fontSize:18 }}>{dim.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12,color:C.white,fontWeight:700 }}>{dim.name}</div>
                  <div style={{ fontSize:9,color:_T.accent,letterSpacing:1 }}>{nl?'GEWICHT':'WEIGHT'}: {dim.weight}</div>
                </div>
                <div style={{ fontSize:13,fontWeight:700,color: d?.pct >= 70 ? '#2A9E82' : d?.pct >= 40 ? '#C8A84B' : '#C05050' }}>
                  {d?.pct ?? 0}%
                </div>
              </div>
              {/* Mini progress bar */}
              <div style={{ height:3,background:`rgba(255,255,255,0.06)`,borderRadius:4,marginBottom:8,overflow:'hidden' }}>
                <div style={{ height:'100%',width:`${d?.pct ?? 0}%`,background:`linear-gradient(90deg,${_T.grad1},${_T.grad2})`,borderRadius:4,transition:'width 0.8s ease' }}/>
              </div>
              <div style={{ fontSize:11,color:C.silver,lineHeight:1.6,marginBottom:4 }}>
                <span style={{ color:`${_T.accent}90` }}>{nl?'Data:':'Data:'} </span>{dim.data}
              </div>
              <div style={{ fontSize:11,color:'#b0c0d8',lineHeight:1.65,marginBottom:5 }}>{dim.science}</div>
              <div style={{ fontSize:10,color:`${_T.accent}60`,fontStyle:'italic',lineHeight:1.5 }}>{dim.source}</div>
            </div>
          );
        })}

        {/* Footer */}
        <div style={{ marginTop:8,padding:'12px 14px',background:`${_T.bg3}`,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10 }}>
          <div style={{ fontSize:10,color:C.silver,lineHeight:1.6,textAlign:'center',fontStyle:'italic' }}>{s.footer}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main gauge component ─────────────────────────────────────────────────────
function SenseMeterGauge({ skills, myPostCount, lang }) {
  const nl = lang === 'nl';
  const [scienceOpen, setScienceOpen] = useState(false);

  const catLevels = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('sl_cat_levels') || '{}'); } catch { return {}; }
  }, []);
  const exHistory = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('sl_ex_history') || '[]'); } catch { return []; }
  }, []);
  const moodLog = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('sl_mood_log') || '[]'); } catch { return []; }
  }, []);
  const sleepLog = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('sl_sleep_log') || '[]'); } catch { return []; }
  }, []);

  const { score, dimensions } = useMemo(() =>
    computeScore({ skills, catLevels, exHistory, moodLog, sleepLog, myPostCount }),
    [skills, catLevels, exHistory, moodLog, sleepLog, myPostCount]
  );

  const needleAngle = scoreToAngle(score);
  const needleTip   = polarToXY(needleAngle, R_OUTER - 16);
  const needleBase1 = polarToXY(needleAngle + 90, 7);
  const needleBase2 = polarToXY(needleAngle - 90, 7);
  const col = zoneColor(score);

  // Tick marks every 20 points
  const ticks = [0, 20, 40, 60, 80, 100].map(v => {
    const a = scoreToAngle(v);
    const inner = polarToXY(a, R_TICK - 8);
    const outer = polarToXY(a, R_TICK);
    const label = polarToXY(a, R_TICK + 11);
    return { v, a, inner, outer, label };
  });

  return (
    <>
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${_T.cardBorderBot}` }}>
        {/* Section label */}
        <div style={{ fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:700,marginBottom:4,textAlign:'center' }}>
          {nl ? 'MAKE SENSE SCORE' : 'MAKE SENSE SCORE'}
        </div>
        <div style={{ fontSize:12,color:C.silver,textAlign:'center',marginBottom:12 }}>
          {nl ? 'Hoe jij overkomt bij anderen' : 'How you come across to others'}
        </div>

        {/* SVG speedometer — viewBox tall enough to show the arch above CY */}
        <svg viewBox="0 0 300 175" style={{ width:'100%',maxWidth:320,display:'block',margin:'0 auto',overflow:'visible' }}>
          <defs>
            <filter id="needleGlow">
              <feGaussianBlur stdDeviation="2" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          {/* Background track — full 180° arch (180→360) */}
          <path d={arcPath(180, 360, R_OUTER, R_INNER)} fill="rgba(255,255,255,0.04)" stroke={_T.cardBorderBot} strokeWidth="0.5"/>

          {/* Coloured zones */}
          {ZONES.map((z, i) => (
            <path key={i} d={arcPath(z.from, z.to, R_OUTER - 2, R_INNER + 2)} fill={`${z.color}30`} />
          ))}

          {/* Filled progress arc — from left (180°) up to needle angle */}
          {score > 0 && (
            <path
              d={arcPath(180, needleAngle, R_OUTER - 4, R_INNER + 4)}
              fill={`${col}50`}
              stroke={`${col}80`}
              strokeWidth="0.5"
            />
          )}

          {/* Tick marks + labels */}
          {ticks.map(t => (
            <g key={t.v}>
              <line x1={t.inner.x} y1={t.inner.y} x2={t.outer.x} y2={t.outer.y} stroke={C.silver} strokeWidth="1" opacity="0.4"/>
              <text x={t.label.x} y={t.label.y} fontSize="7" fill={C.silver} textAnchor="middle" dominantBaseline="middle" opacity="0.5">{t.v}</text>
            </g>
          ))}

          {/* Needle */}
          <polygon
            points={`${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`}
            fill={col}
            filter="url(#needleGlow)"
            opacity="0.9"
          />
          {/* Needle hub */}
          <circle cx={CX} cy={CY} r="7" fill={col} opacity="0.9"/>
          <circle cx={CX} cy={CY} r="3.5" fill="#060d1a"/>

          {/* Score — centred inside the arch (above needle hub) */}
          <text x={CX} y={CY - 30} fontSize="30" fontWeight="900" fill={col} textAnchor="middle" dominantBaseline="middle" fontFamily="Georgia,serif">{score}</text>
          <text x={CX} y={CY - 12} fontSize="9" fill={col} textAnchor="middle" dominantBaseline="middle" letterSpacing="1" opacity="0.8">/100</text>

          {/* Zone label — just below hub */}
          <text x={CX} y={CY + 16} fontSize="9.5" fill={C.white} textAnchor="middle" dominantBaseline="middle" fontWeight="700" opacity="0.9">
            {scoreLabel(score, nl)}
          </text>

          {/* Left / Right end labels */}
          <text x={CX - R_OUTER - 4} y={CY + 4} fontSize="7.5" fill={C.silver} textAnchor="middle" opacity="0.4">0</text>
          <text x={CX + R_OUTER + 4} y={CY + 4} fontSize="7.5" fill={C.silver} textAnchor="middle" opacity="0.4">100</text>
        </svg>

        {/* Dimension mini-bars */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 12px',marginTop:10,padding:'0 4px' }}>
          {dimensions.map((d, i) => {
            const icons   = ['🔍','❤️','🎯','✨','📅','🗣️'];
            const labelsEN = ['Self-Awareness','Emotion Reg.','Mental Clarity','Social Presence','Consistency','Expression'];
            const labelsNL = ['Zelfbewustzijn','Emotiereg.','Mentale Helderheid','Soc. Aanwezigheid','Consistentie','Expressie'];
            return (
              <div key={d.id}>
                <div style={{ display:'flex',alignItems:'center',gap:4,marginBottom:2 }}>
                  <span style={{ fontSize:10 }}>{icons[i]}</span>
                  <span style={{ fontSize:9,color:C.silver,flex:1 }}>{nl ? labelsNL[i] : labelsEN[i]}</span>
                  <span style={{ fontSize:9,color:_T.accent,fontWeight:700 }}>{d.pct}%</span>
                </div>
                <div style={{ height:3,background:'rgba(255,255,255,0.06)',borderRadius:3,overflow:'hidden' }}>
                  <div style={{ height:'100%',width:`${d.pct}%`,background:`linear-gradient(90deg,${_T.grad1},${_T.grad2})`,borderRadius:3,transition:'width 0.8s ease' }}/>
                </div>
              </div>
            );
          })}
        </div>

        {/* Science link */}
        <button
          onClick={() => setScienceOpen(true)}
          style={{
            display:'block',width:'100%',marginTop:14,
            background:'none',border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,
            padding:'9px 14px',cursor:'pointer',textAlign:'center',
            color:`${_T.accent}`,fontSize:11,letterSpacing:0.5,fontWeight:600,
          }}
        >
          {nl ? '🔬 Bekijk de wetenschappelijke onderbouwing →' : '🔬 View the science behind this score →'}
        </button>
      </div>

      {scienceOpen && (
        <ScienceModal
          score={score}
          dimensions={dimensions}
          lang={lang}
          onClose={() => setScienceOpen(false)}
        />
      )}
    </>
  );
}

export default SenseMeterGauge;
